
import Vue from 'vue';
import _clamp from 'lodash/clamp';
import { featureTourService } from '../services/featureTourService';
import FeatureTourCard from './FeatureTourCard.vue';
import FeatureTourMedia from './FeatureTourMedia.vue';

const VIEWPORT_MARGIN = 16;

export default Vue.extend({
	name: 'FeatureTourShell',
	components: { FeatureTourCard, FeatureTourMedia },
	data () {
		return {
			cardWidth: 0,
			cardHeight: 0,
			resizeObserver: null as ResizeObserver | null,
			closePhase: 'idle' as 'idle' | 'arm' | 'fly',
			closeFromRect: null as { top: number; left: number; width: number; height: number } | null,
			closeToRect: null as { top: number; left: number; width: number; height: number } | null,
			theaterMode: false,
		};
	},
	computed: {
		serviceState () {
			return featureTourService.state;
		},
		// FeatureTourCard expects a 0-based step index. The service tracks a
		// 1-based displayStepIndex (counting only canShow-visible steps, so
		// the user sees "Step 02" when they're really on raw step 7 with 5
		// hidden in between). Convert here, clamping into [0, totalSteps - 1]
		// for the brief moment before the first step's onHighlightStarted
		// populates the value, and as a guard against transient overshoot.
		displayStepIndexForCard (): number {
			const total = this.serviceState.displayTotalSteps || 0;
			const max = Math.max(0, total - 1);
			return _clamp((this.serviceState.displayStepIndex || 0) - 1, 0, max);
		},
		theaterIsLastStep (): boolean {
			const tour = this.serviceState.activeTour;
			const idx = this.serviceState.activeStepIndex;
			return !!tour && idx === tour.steps.length - 1;
		},
		cardContainerStyles (): any {
			if (this.closePhase !== 'idle') {
				const closeTransition = 'top 0.5s cubic-bezier(0.5, 0, 0.75, 0), '
					+ 'left 0.5s cubic-bezier(0.5, 0, 0.75, 0), '
					+ 'transform 0.5s cubic-bezier(0.5, 0, 0.75, 0), '
					+ 'opacity 0.45s cubic-bezier(0.4, 0, 1, 1)';

				if (this.closePhase === 'arm' && this.closeFromRect) {
					const r = this.closeFromRect;
					return {
						position: 'fixed',
						top: `${r.top + r.height / 2}px`,
						left: `${r.left + r.width / 2}px`,
						transform: 'translate(-50%, -50%) scale(1)',
						opacity: 1,
						transition: closeTransition,
						pointerEvents: 'none',
						transformOrigin: 'center center',
					};
				}
				if (this.closePhase === 'fly' && this.closeToRect) {
					const r = this.closeToRect;
					return {
						position: 'fixed',
						top: `${r.top + r.height / 2}px`,
						left: `${r.left + r.width / 2}px`,
						transform: 'translate(-50%, -50%) scale(0.06)',
						opacity: 0,
						transition: closeTransition,
						pointerEvents: 'none',
						transformOrigin: 'center center',
					};
				}
			}

			const rect = this.serviceState.targetRect;
			const step = this.serviceState.activeStep;

			if (!rect || !step) return { display: 'none' };

			const placement = step.placement || 'right';
			const offset = 20;

			if (this.$vuetify.breakpoint.smAndDown) {
				return {
					position: 'fixed',
					bottom: '20px',
					left: '50%',
					transform: 'translateX(-50%)',
					width: '92vw',
					transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
				};
			}

			const vw = window.innerWidth;
			const vh = window.innerHeight;
			const cardW = this.cardWidth || 440;
			const cardH = this.cardHeight || 520;

			const centerY = rect.top + (rect.height / 2);
			const centerX = rect.left + (rect.width / 2);

			let top = 0;
			let left = 0;
			let transform = 'translate(0, 0)';

			if (placement === 'right' || placement === 'left') {
				const minTop = VIEWPORT_MARGIN + cardH / 2;
				const maxTop = vh - VIEWPORT_MARGIN - cardH / 2;
				top = Math.min(Math.max(centerY, minTop), maxTop);

				// Clamp horizontally so the card stays on-screen. When the
				// ideal placement side doesn't have room, flip to the other
				// side; if neither fits, hard-clamp against the viewport edge.
				const roomRight = vw - rect.right - offset;
				const roomLeft = rect.left - offset;

				if (placement === 'right') {
					if (roomRight >= cardW + VIEWPORT_MARGIN) {
						left = rect.right + offset;
						transform = 'translate(0, -50%)';
					} else if (roomLeft >= cardW + VIEWPORT_MARGIN) {
						left = rect.left - offset;
						transform = 'translate(-100%, -50%)';
					} else {
						left = Math.min(Math.max(rect.right + offset, VIEWPORT_MARGIN), vw - VIEWPORT_MARGIN - cardW);
						transform = 'translate(0, -50%)';
					}
				} else {
					if (roomLeft >= cardW + VIEWPORT_MARGIN) {
						left = rect.left - offset;
						transform = 'translate(-100%, -50%)';
					} else if (roomRight >= cardW + VIEWPORT_MARGIN) {
						left = rect.right + offset;
						transform = 'translate(0, -50%)';
					} else {
						left = Math.min(Math.max(rect.left - offset, VIEWPORT_MARGIN + cardW), vw - VIEWPORT_MARGIN);
						transform = 'translate(-100%, -50%)';
					}
				}
			} else if (placement === 'bottom' || placement === 'top') {
				const minLeft = VIEWPORT_MARGIN + cardW / 2;
				const maxLeft = vw - VIEWPORT_MARGIN - cardW / 2;
				left = Math.min(Math.max(centerX, minLeft), maxLeft);

				const roomBelow = vh - rect.bottom - offset;
				const roomAbove = rect.top - offset;

				if (placement === 'bottom') {
					if (roomBelow >= cardH + VIEWPORT_MARGIN) {
						top = rect.bottom + offset;
						transform = 'translate(-50%, 0)';
					} else if (roomAbove >= cardH + VIEWPORT_MARGIN) {
						top = rect.top - offset;
						transform = 'translate(-50%, -100%)';
					} else {
						top = Math.min(Math.max(rect.bottom + offset, VIEWPORT_MARGIN), vh - VIEWPORT_MARGIN - cardH);
						transform = 'translate(-50%, 0)';
					}
				} else {
					if (roomAbove >= cardH + VIEWPORT_MARGIN) {
						top = rect.top - offset;
						transform = 'translate(-50%, -100%)';
					} else if (roomBelow >= cardH + VIEWPORT_MARGIN) {
						top = rect.bottom + offset;
						transform = 'translate(-50%, 0)';
					} else {
						top = Math.min(Math.max(rect.top - offset, VIEWPORT_MARGIN + cardH), vh - VIEWPORT_MARGIN);
						transform = 'translate(-50%, -100%)';
					}
				}
			} else {
				top = centerY;
				left = centerX;
				transform = 'translate(-50%, -50%)';
			}

			return {
				position: 'fixed',
				top: `${top}px`,
				left: `${left}px`,
				transform,
				transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
				pointerEvents: 'auto',
			};
		},
	},
	mounted () {
		if (this.$el && this.$el.parentNode !== document.body) {
			document.body.appendChild(this.$el);
		}
		this.setupResizeObserver();
		window.addEventListener('resize', this.onWindowResize);
		document.addEventListener('keydown', this.onGlobalKeyDown);
	},
	beforeDestroy () {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
		window.removeEventListener('resize', this.onWindowResize);
		document.removeEventListener('keydown', this.onGlobalKeyDown);
		if (this.$el && this.$el.parentNode === document.body) {
			document.body.removeChild(this.$el);
		}
	},
	watch: {
		'serviceState.activeStepIndex' () {
			this.$nextTick(() => this.measureCard());
		},
		'serviceState.isActive' (active: boolean) {
			if (active) {
				this.$nextTick(() => {
					this.setupResizeObserver();
					this.measureCard();
				});
			} else {
				// If the tour was dismissed/completed while theater mode
				// was open (e.g. via the service from outside), drop the
				// flag so it doesn't reappear if the user restarts the
				// tour from the launcher.
				this.theaterMode = false;
			}
		},
	},
	methods: {
		setupResizeObserver () {
			if (this.resizeObserver) return;
			const el = this.$refs.cardContainer as HTMLElement | undefined;
			if (!el || typeof ResizeObserver === 'undefined') return;
			this.resizeObserver = new ResizeObserver(() => this.measureCard());
			this.resizeObserver.observe(el);
		},
		measureCard () {
			const el = this.$refs.cardContainer as HTMLElement | undefined;
			if (!el) return;
			const r = el.getBoundingClientRect();
			if (r.width && r.height) {
				this.cardWidth = r.width;
				this.cardHeight = r.height;
			}
		},
		onWindowResize () {
			this.measureCard();
		},
		onNext () {
			const tour = this.serviceState.activeTour;
			const idx = this.serviceState.activeStepIndex;
			const isLastStep = !!tour && idx === tour.steps.length - 1;

			if (isLastStep) {
				// Run the same fly-to-launcher animation we use for close, but
				// finish via complete() so the tour is marked completed.
				this.runCloseAnimation(() => featureTourService.complete());
				return;
			}
			featureTourService.next();
		},
		onBack () {
			featureTourService.previous();
		},
		onClose () {
			this.runCloseAnimation(() => featureTourService.stop());
		},
		onExpand () {
			this.theaterMode = true;
		},
		onTheaterClose () {
			this.theaterMode = false;
		},
		onTheaterNext () {
			if (this.theaterIsLastStep) {
				// Finishing from theater mode skips the fly-to-launcher
				// animation — the launcher is hidden behind the theater
				// backdrop anyway, so animating to it would just look like
				// a fade. Close the modal first, then mark the tour
				// completed via the service.
				this.theaterMode = false;
				featureTourService.complete();
				return;
			}
			featureTourService.next();
		},
		onTheaterBack () {
			featureTourService.previous();
		},
		onGlobalKeyDown (e: KeyboardEvent) {
			if (e.key === 'Escape' && this.theaterMode) {
				e.preventDefault();
				e.stopPropagation();
				this.theaterMode = false;
			}
		},
		runCloseAnimation (finalAction: () => void) {
			const launcherEl = document.querySelector('[data-feature-tour-launcher]') as HTMLElement | null;
			const cardEl = this.$refs.cardContainer as HTMLElement | undefined;
			if (!launcherEl || !cardEl) {
				finalAction();
				return;
			}

			const cardRect = cardEl.getBoundingClientRect();
			const launcherRect = launcherEl.getBoundingClientRect();

			this.closeFromRect = {
				top: cardRect.top,
				left: cardRect.left,
				width: cardRect.width,
				height: cardRect.height,
			};
			this.closeToRect = {
				top: launcherRect.top,
				left: launcherRect.left,
				width: launcherRect.width,
				height: launcherRect.height,
			};

			// Phase 1: lock the card to its current geometry with the close
			// transition rule already in place.
			this.closePhase = 'arm';

			// Phase 2: after Vue commits the arm styles and the browser paints,
			// flip to 'fly'. Changing the transition rule and target values in
			// the same paint cycle reliably suppresses the transition, so the
			// rule must be committed before values change. We use nextTick +
			// setTimeout (instead of rAF) because rAF is paused in background
			// tabs, which would freeze the close in the 'arm' state.
			this.$nextTick(() => {
				void cardEl.offsetWidth;
				window.setTimeout(() => {
					this.closePhase = 'fly';
				}, 32);
			});

			window.setTimeout(() => {
				launcherEl.classList.add('is-arrival-pulse');
				window.setTimeout(() => launcherEl.classList.remove('is-arrival-pulse'), 650);
			}, 480);

			window.setTimeout(() => {
				this.closePhase = 'idle';
				this.closeFromRect = null;
				this.closeToRect = null;
				finalAction();
			}, 580);
		},
	},
});
